import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';

function splitFragment(url: string): { url: string; fragment: string } {
  const index = url.indexOf('#');

  if (index === -1) {
    return { url, fragment: '' };
  }

  return { url: url.slice(0, index), fragment: url.slice(index) };
}

export function buildPathWithQuery(
  path: string,
  query?: Record<string, string | string[]>,
): string {
  if (!query || Object.keys(query).length === 0) {
    return path;
  }
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((val) => params.append(key, val));
    } else {
      params.append(key, value);
    }
  });
  const queryString = params.toString();
  if (!queryString) {
    return path;
  }
  const { url: pathname, fragment } = splitFragment(path);
  const separator = pathname.includes('?') ? '&' : '?';
  return `${pathname}${separator}${queryString}${fragment}`;
}

interface BuildPersistedParamsOptions {
  searchParams: Record<string, string | null>;
  cityOverride?: string;
}

export function buildPersistedParamsQueryString({ searchParams, cityOverride }: BuildPersistedParamsOptions): string {
  const params = new URLSearchParams();

  const city = cityOverride || searchParams[PersistedUserNavigationSearchParams.CIDADE];
  if (city) {
    params.append(PersistedUserNavigationSearchParams.CIDADE, city);
  }

  Object.values(PersistedUserNavigationSearchParams).forEach((key) => {
    if (key === PersistedUserNavigationSearchParams.CIDADE) {
      return;
    }
    const value = searchParams[key];
    if (value) {
      params.append(key, value);
    }
  });

  return params.toString();
}

export function appendPersistedParamsToUrl(baseUrl: string, queryString: string): string {
  if (!queryString) {
    return baseUrl;
  }
  const { url, fragment } = splitFragment(baseUrl);
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${queryString}${fragment}`;
}

export interface BuildUrlWithPersistedParamsOptions {
  baseUrl: string;
  overrides?: Partial<Record<string, string | null>>;
  /** When provided (e.g. from useSearchParams in a hook), use these instead of window.location. Ensures links get current URL params during SSR and client. */
  searchParams?: Record<string, string | null>;
}

function getPathnameOnly(url: string): string {
  const idx = url.indexOf('?');
  return idx === -1 ? url : url.slice(0, idx);
}

export function buildUrlWithPersistedParams(
  { baseUrl, overrides, searchParams: searchParamsFromCaller }: BuildUrlWithPersistedParamsOptions,
): string {
  let searchParams: Record<string, string | null>;

  if (searchParamsFromCaller) {
    searchParams = { ...searchParamsFromCaller };
  } else {
    const currentParams = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : '',
    );
    searchParams = {};
    Object.values(PersistedUserNavigationSearchParams).forEach((key) => {
      const value = currentParams.get(key);
      searchParams[key] = value || null;
    });
  }

  const mergedParams: Record<string, string | null> = { ...searchParams };
  if (overrides) {
    Object.entries(overrides).forEach(([key, value]) => {
      if (value !== undefined) {
        mergedParams[key] = value as string | null;
      }
    });
  }

  const { url: baseUrlWithoutFragment, fragment } = splitFragment(baseUrl);
  const pathnameOnly = getPathnameOnly(baseUrlWithoutFragment);
  const persistedQueryString = buildPersistedParamsQueryString({ searchParams: mergedParams });

  const persistedKeys = new Set<string>(Object.values(PersistedUserNavigationSearchParams));
  const existingSearch = baseUrlWithoutFragment.includes('?')
    ? baseUrlWithoutFragment.slice(baseUrlWithoutFragment.indexOf('?') + 1)
    : '';
  const existingParams = new URLSearchParams(existingSearch);
  const combined = new URLSearchParams(persistedQueryString);
  existingParams.forEach((value, key) => {
    if (!persistedKeys.has(key)) {
      combined.append(key, value);
    }
  });

  const queryString = combined.toString();
  if (!queryString) {
    return `${pathnameOnly}${fragment}`;
  }
  return `${pathnameOnly}?${queryString}${fragment}`;
}
